import React, { useEffect, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { useToast } from "@shared/hooks/use-toast";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useUpdatePrintStatus, useVendorList } from "../../hooks";
import { logEvent } from "@shared/analytics/analytics";
import dayjs from "dayjs";

interface EditPrintingStatusModalProps {
  selectedCycle: any;
  openModal: boolean;
  openChangeModal: (open: boolean) => void;
}

const EditPrintingStatusModal = ({
  selectedCycle,
  openModal,
  openChangeModal,
}: EditPrintingStatusModalProps) => {
  const { toast } = useToast();
  const updatePrintStatus = useUpdatePrintStatus();
  const { data: vendorlist = [] } = useVendorList();
  console.log(selectedCycle,'vendorlist')

  const parseFlexibleDateToString = (input: any) => {
    if (!input) return "";

    const knownFormats = [
      "YYYY-MM-DD",
      "DD MMM YYYY",
      "MMM DD YYYY",
      "DD/MM/YYYY",
      "MM/DD/YYYY",
      "YYYY/MM/DD",
      "DD-MM-YYYY",
      "YYYY-MM-DDTHH:mm:ss.SSSZ",
      "YYYY-MM-DDTHH:mm:ssZ",
      "ddd MMM DD YYYY HH:mm:ss [GMT]ZZ (z)",
    ];

    for (const format of knownFormats) {
      const date = dayjs(input, format, true);
      if (date.isValid()) return date.format("YYYY-MM-DD");
    }

    const fallback = dayjs(input);
    return fallback.isValid() ? fallback.format("YYYY-MM-DD") : "";
  };

  // Determine status options based on current status
  const getStatusOptions = () => {
    const currentStatus = selectedCycle?.printingStatus;

    const allStatuses = [
      { value: "Pending", label: "Pending" },
      { value: "In Progress", label: "Started" },
      { value: "Completed", label: "Completed" },
    ];

    // // If completed, only show completed
    // if (currentStatus === "Completed") {
    //   return allStatuses.filter((s) => s.value === "Completed");
    // }

    // // If in progress, show in progress and completed
    // if (currentStatus === "In Progress") {
    //   return allStatuses.filter((s) => s.value !== "Pending");
    // }

    // If pending, show all
    return allStatuses;
  };

  // Form fields definition
  const formFields: FormField[] = useMemo(() => [
    {
      name: "printDate",
      type: "text",
      label: "Print Date",
      placeholder: "Print date",
      required: false,
      disabled: true,
      className: "col-span-2 mt-3",
    },
    {
      name: "status",
      type: "select",
      size: "medium",
      variant: "outlined",
      label: "Status",
      placeholder: "Select status",
      required: true,
      options: getStatusOptions(),
      helperText: "Update the printing status for this cycle",
      className: "col-span-2 mt-3",
    },
    {
      name: "vendor",
      type: "select",
      label: "Vendor",
      placeholder: "Select vendor",
      required: true,
      options: vendorlist?.map((vendor: any) => ({
        value: vendor.value?.toString(),
        label: vendor.label,
      })),
      disabled: selectedCycle?.printingStatus === "Completed",
      className: "col-span-1 mt-3",
    },
    {
      name: "lapsedDays",
      type: "text",
      label: "Lapsed Days",
      placeholder: "Lapsed days",
      required: false,
      disabled: true,
      className: "col-span-1 mt-3",
    },
  ], [vendorlist, selectedCycle?.printingStatus]);

  // Create form with proper default values
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      printDate: "",
      status: [],
      vendor: "",
      lapsedDays: "",
    },
  });

  // Populate form when modal opens or selected cycle changes
  useEffect(() => {
    if (openModal && selectedCycle) {
      const initialValues = {
        printDate: selectedCycle.printingDate || "",
        status: [selectedCycle.printingStatus || ""],
        vendor:  selectedCycle?.vendor?.id?.toString() || "",
        lapsedDays: selectedCycle.lapsedDays
          ? `${selectedCycle.lapsedDays} days`
          : "0 days",
      };

      FormService.populateForm(form, initialValues, formFields);
    }
  }, [openModal, selectedCycle]);

  // Handle form submission
  const handleSubmit = async (formValues: any) => {
    try {
      const newStatus = formValues.status;
      
      const selectedVendor = vendorlist?.find(
        (v: any) => v.value?.toString() === formValues.vendor?.toString()
      );

      await updatePrintStatus.mutateAsync({
        runId: selectedCycle.runId,
        payload: {
          ...(newStatus !== "Completed" ? {
            print_date: selectedCycle.printingStatus === "Pending"
              ? dayjs().format("YYYY-MM-DD") 
              : parseFlexibleDateToString(selectedCycle.printingDate),
          } : {}),
          print_status: newStatus,
          vendor: {
            id: formValues.vendor,
            vendor_name: selectedVendor?.label || "",
          },
          ...(newStatus === "Completed" ? { complete_date: dayjs().format("YYYY-MM-DD") } : {}),
        },
      });

      logEvent(`BX Status Update`, {
        print_status: newStatus,
        runId: selectedCycle.runId,
      });

      await queryClient.invalidateQueries({
        queryKey: QueryKeyFactory.module.bx.printingDistribution.printList(),
      });

      toast({
        title: "Status Updated",
        description: `Cycle has been marked as ${
          newStatus === "In Progress" ? "started" : newStatus.toLowerCase()
        }`,
      });

      handleClose();
    } catch (error) {
      console.error("Form submission error:", error);
      toast({
        title: "Error",
        description: "Failed to update status. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleClose = () => {
    form.reset();
    openChangeModal(false);
  };

  return (
    <Dialog open={openModal} onOpenChange={openChangeModal}>
      <DialogContent className="sm:max-w-[500px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">Edit Printing Status</DialogTitle>
          <DialogDescription>
            Update the status and details for {selectedCycle?.cycleName}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            onReset={handleClose}
            gridColumns={2}
            responsiveColumns={{
              sm: 1,
              md: 2,
            }}
            config={{
              showResetButton: true,
              showSubmitButton: true,
              submitButtonText: updatePrintStatus.isPending
                ? "Updating..."
                : "Update Status",
              resetButtonText: "Cancel",
              spacing: "comfortable",
              classes: {
                container: "w-full mb-4",
                label: "block text-sm font-medium text-gray-700 mb-2",
                buttonContainer: "flex justify-end",
                input:
                  "w-full h-11 px-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
                select:
                  "w-full h-11 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
                textarea:
                  "w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
                error: "text-red-600 text-sm mt-1",
                helperText: "text-xs text-gray-500 mt-1",
                number:
                  "w-full h-11 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
                "multi-select":
                  "w-full h-11 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
                file:
                  " w-full flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-12 text-center " +
                  "hover:border-gray-400 transition-colors cursor-pointer bg-white",
                documents:
                  "w-full flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-12 text-center " +
                  "hover:border-gray-400 transition-colors cursor-pointer bg-white",
              },
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditPrintingStatusModal;
